import { SATError, SATErrorModel, SATResult } from "@sadtx/sat-error";
import { computeCharge } from "@/store/store-format";
import {
  StoreCreateOrderResponseDto,
  StoreGuardianSearchResponseDto,
  StorePatientContextResponseDto,
  StorePaymentHistoryPageDto,
  StorePaymentHistoryRecordDto,
  StoreProductResponseDto,
  StorePurchaseResponseDto,
  StoreOrderReceiptResponseDto,
  StoreRefundQuoteResponseDto,
  StoreRefundRequestResponseDto,
  StoreRefundResponseDto,
  StoreSettlementPageDto,
  RevokeTreatmentVoucherResponseDto,
  TreatmentVoucherClaimResponseDto,
  VoucherRefundAttestationResponseDto,
  StoreBrandpayCustomerResponseDto,
  StoreBrandpayMethodsResponseDto,
} from "../dto/store-response-dtos";
import {
  StoreConfirmPurchaseRequestPayload,
  StoreGuardianSearchRequestPayload,
  StorePaymentHistoryRequestPayload,
  StorePrepareOrderRequestPayload,
  StoreRefundRequestCreatePayload,
  StoreRefundRequestDecisionPayload,
  StoreRefundRequestPayload,
  StoreSettlementRequestPayload,
  RevokeTreatmentVoucherRequestPayload,
  TreatmentVoucherClaimRequestPayload,
  VoucherOrderIdRequestPayload,
} from "../payload/store-request-payloads";
import { StoreSource } from "./store-source";

/**
 * 백엔드 결제 API 구현 전 화면 개발/데모용 목 소스.
 * VITE_STORE_USE_MOCK=true 일 때만 사용된다. (store-config.ts 참고)
 *
 * NOTE: 가격은 임시 값이다. 실제 판매가는 정책 확정 후 서버 상품 데이터로 관리한다.
 */


/** 목 예외 환불 요청 (SPA 세션 동안 유지) */
const mockRefundRequests: StoreRefundRequestResponseDto[] = [];

const MOCK_PRODUCTS: StoreProductResponseDto[] = [
  {
    productId: "SAT001-TX-3M",
    type: "TREATMENT",
    months: 3,
    name: "MyoHabit™ 치료 이용권 3개월",
    description: "소아근시 디지털치료기기(DTx) MyoHabit™ 치료 프로그램 3개월 이용권",
    price: 150000,
    originalPrice: null,
    vatIncluded: true,
  },
  {
    productId: "SAT001-TX-6M",
    type: "TREATMENT",
    months: 6,
    name: "MyoHabit™ 치료 이용권 6개월",
    description: "소아근시 디지털치료기기(DTx) MyoHabit™ 치료 프로그램 6개월 이용권",
    price: 250000,
    originalPrice: 300000,
    vatIncluded: true,
  },
  {
    productId: "SAT001-TX-12M",
    type: "TREATMENT",
    months: 12,
    name: "MyoHabit™ 치료 이용권 12개월",
    description: "소아근시 디지털치료기기(DTx) MyoHabit™ 치료 프로그램 12개월 이용권",
    price: 480000,
    originalPrice: 600000,
    vatIncluded: true,
  },
  {
    productId: "SAT001-GA-3M",
    type: "GUARDIAN_APP",
    months: 3,
    name: "보호자용 앱 이용권 3개월",
    description: "자녀 치료 현황을 확인할 수 있는 보호자용 앱 3개월 이용권",
    price: 30000,
    originalPrice: null,
    vatIncluded: true,
  },
  {
    productId: "SAT001-GA-6M",
    type: "GUARDIAN_APP",
    months: 6,
    name: "보호자용 앱 이용권 6개월",
    description: "자녀 치료 현황을 확인할 수 있는 보호자용 앱 6개월 이용권",
    price: 54000,
    originalPrice: null,
    vatIncluded: true,
  },
  {
    productId: "SAT001-GA-12M",
    type: "GUARDIAN_APP",
    months: 12,
    name: "보호자용 앱 이용권 12개월",
    description: "자녀 치료 현황을 확인할 수 있는 보호자용 앱 12개월 이용권",
    price: 96000,
    originalPrice: null,
    vatIncluded: true,
  },
];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class StoreMockSource implements StoreSource {
  async fetchProducts(): Promise<SATResult<StoreProductResponseDto[], SATError>> {
    await delay(300);
    console.log("[StoreMockSource] :: fetchProducts :: 목 상품 목록 반환");
    return SATResult.success(MOCK_PRODUCTS);
  }

  async fetchBrandpayCustomer(): Promise<SATResult<StoreBrandpayCustomerResponseDto, SATError>> {
    await delay(100);
    return SATResult.success({
      customerKey: "mock-customer-key",
      flow: "brandpay",
    });
  }

  async fetchBrandpayMethods(): Promise<SATResult<StoreBrandpayMethodsResponseDto, SATError>> {
    await delay(100);
    return SATResult.success({
      customerKey: "mock-customer-key",
      cards: [
        {
          methodKey: "mock-method-1",
          alias: "병원 법인카드",
          cardName: "신한카드",
          cardNumber: "5327-****-****-1234",
          cardType: "신용",
          ownerType: "법인",
          status: "ENABLED",
        },
      ],
      accounts: [],
    });
  }

  async resolvePatientContext(
    patientUuid: string
  ): Promise<SATResult<StorePatientContextResponseDto, SATError>> {
    await delay(300);
    console.log("[StoreMockSource] :: resolvePatientContext :: 목 환자 컨텍스트", patientUuid);
    return SATResult.success({
      guardianUuid: MOCK_GUARDIAN.guardianUuid,
      guardianName: MOCK_GUARDIAN.name,
      guardianMaskedPhone: MOCK_GUARDIAN.maskedPhone,
      patientUuid,
      patientName: MOCK_GUARDIAN.children[0].name,
      patientBirthDate: "2017-03-05",
      hasUnusedVoucher: false,
    });
  }

  async searchGuardian(
    payload: StoreGuardianSearchRequestPayload
  ): Promise<SATResult<StoreGuardianSearchResponseDto | null, SATError>> {
    await delay(500);
    // 목 모드: 이름/전화번호가 형식만 맞으면 목 보호자를 반환
    if (!payload.guardianName || payload.guardianPhone.length < 10) {
      return SATResult.success(null);
    }
    console.log("[StoreMockSource] :: searchGuardian :: 목 보호자 반환");
    return SATResult.success({ ...MOCK_GUARDIAN, name: payload.guardianName });
  }

  async prepareOrder(
    payload: StorePrepareOrderRequestPayload
  ): Promise<SATResult<StoreCreateOrderResponseDto, SATError>> {
    await delay(300);
    const product = MOCK_PRODUCTS.find((p) => p.productId === payload.productId);
    const orderId = `MOCK-ORD-${Date.now()}`;
    return SATResult.success({
      orderId,
      orderName: product?.name ?? "알 수 없는 상품",
      amount: product ? computeCharge(product).amount : 0,
      supplyAmount: product ? computeCharge(product).supplyAmount : 0,
      vatAmount: product ? computeCharge(product).vatAmount : 0,
      currency: "KRW",
    });
  }

  async confirmPurchase(
    payload: StoreConfirmPurchaseRequestPayload
  ): Promise<SATResult<StorePurchaseResponseDto, SATError>> {
    await delay(500);
    console.log("[StoreMockSource] :: confirmPurchase :: 목 결제 승인+이용권 발급", payload);
    return SATResult.success({
      orderId: payload.orderId,
      voucherIssued: true,
      receipt: `MOCK-RECEIPT-${payload.orderId}`,
      receiptUrl: null,
      approvedAt: new Date().toISOString(),
    });
  }

  async claimTreatmentVoucher(
    payload: TreatmentVoucherClaimRequestPayload
  ): Promise<SATResult<TreatmentVoucherClaimResponseDto, SATError>> {
    await delay(300);
    console.log("[StoreMockSource] :: claimTreatmentVoucher :: 목 자격 발급", payload.receipt);
    return SATResult.success({
      voucherUuid: `MOCK-VCH-${Date.now()}`,
      orderId: payload.receipt.replace("MOCK-RECEIPT-", ""),
      months: 3,
      alreadyClaimed: false,
    });
  }

  async markOrderClaimed(): Promise<SATResult<void, SATError>> {
    return SATResult.success(undefined);
  }

  async fetchOrderReceipt(
    orderId: string
  ): Promise<SATResult<StoreOrderReceiptResponseDto, SATError>> {
    await delay(200);
    return SATResult.success({ orderId, receipt: `MOCK-RECEIPT-${orderId}`, claimedAt: null });
  }

  async requestVoucherRefundAttestation(
    payload: VoucherOrderIdRequestPayload
  ): Promise<SATResult<VoucherRefundAttestationResponseDto, SATError>> {
    await delay(200);
    return SATResult.success({
      orderId: payload.orderId,
      attestation: `MOCK-ATTESTATION-${payload.orderId}`,
      expiresIn: 600,
    });
  }

  async cancelVoucherRefund(): Promise<SATResult<void, SATError>> {
    return SATResult.success(undefined);
  }

  async quoteRefund(
    orderId: string,
    payload: StoreRefundRequestPayload
  ): Promise<SATResult<StoreRefundQuoteResponseDto, SATError>> {
    await delay(200);
    const record = MOCK_PAYMENT_HISTORY.find((r) => r.orderId === orderId);
    console.log("[StoreMockSource] :: quoteRefund", payload.attestation);
    return SATResult.success({
      orderId,
      amount: record?.amount ?? 0,
      refundType: "WITHDRAWAL",
      totalDays: null,
      remainingDays: null,
      withdrawalDeadline: null,
    });
  }

  async createRefundRequest(
    orderId: string,
    payload: StoreRefundRequestCreatePayload
  ): Promise<SATResult<StoreRefundRequestResponseDto, SATError>> {
    await delay(200);
    const record = MOCK_PAYMENT_HISTORY.find((r) => r.orderId === orderId);
    const request: StoreRefundRequestResponseDto = {
      id: mockRefundRequests.length + 1,
      orderId,
      hospitalCode: "MOCK",
      hospitalName: "서울밝은눈안과",
      productName: record?.productName ?? "",
      patientNameMasked: record?.patientNameMasked ?? "",
      amount: record?.amount ?? 0,
      orderStatus: record?.status ?? null,
      reason: payload.reason,
      detail: payload.detail,
      status: "REQUESTED",
      createdAt: new Date().toISOString(),
      decidedAt: null,
      decisionNote: null,
    };
    mockRefundRequests.unshift(request);
    if (record) {
      record.refundRequest = { id: request.id, status: "REQUESTED", reason: payload.reason, createdAt: request.createdAt };
    }
    return SATResult.success(request);
  }

  async fetchRefundRequests(
    status: string | null
  ): Promise<SATResult<StoreRefundRequestResponseDto[], SATError>> {
    await delay(200);
    return SATResult.success(
      mockRefundRequests.filter((r) => !status || r.status === status).map((r) => ({ ...r }))
    );
  }

  async decideRefundRequest(
    requestId: number,
    payload: StoreRefundRequestDecisionPayload
  ): Promise<SATResult<StoreRefundRequestResponseDto, SATError>> {
    await delay(200);
    const request = mockRefundRequests.find((r) => r.id === requestId);
    if (!request) {
      return SATResult.failure(new SATErrorModel.FailToGetItem("환불 요청을 찾을 수 없습니다."));
    }
    request.status = payload.decision === "APPROVE" ? "APPROVED" : "REJECTED";
    request.decidedAt = new Date().toISOString();
    request.decisionNote = payload.note ?? null;
    const record = MOCK_PAYMENT_HISTORY.find((r) => r.orderId === request.orderId);
    if (record) {
      record.refundRequest = { id: request.id, status: request.status, reason: request.reason, createdAt: request.createdAt };
      record.exceptionApproved = request.status === "APPROVED";
    }
    return SATResult.success({ ...request });
  }

  async refundOrder(
    orderId: string,
    payload: StoreRefundRequestPayload
  ): Promise<SATResult<StoreRefundResponseDto, SATError>> {
    await delay(400);
    console.log("[StoreMockSource] :: refundOrder :: 목 환불", orderId, payload.attestation);
    const record = MOCK_PAYMENT_HISTORY.find((r) => r.orderId === orderId);
    if (record) {
      record.status = "REFUNDED";
      record.refundedAt = new Date().toISOString();
      record.refundAmount = record.amount;
      record.refundType = "WITHDRAWAL";
      record.refundable = false;
    }
    return SATResult.success({
      orderId,
      refundedAt: new Date().toISOString(),
      amount: record?.amount ?? 0,
      refundType: "WITHDRAWAL",
      refundReceipt: `MOCK-REFUND-RECEIPT-${orderId}`,
      alreadyRefunded: false,
    });
  }

  async revokeTreatmentVoucher(
    payload: RevokeTreatmentVoucherRequestPayload
  ): Promise<SATResult<RevokeTreatmentVoucherResponseDto, SATError>> {
    await delay(200);
    return SATResult.success({
      voucherUuid: `MOCK-VCH-${payload.refundReceipt.slice(-6)}`,
      alreadyRevoked: false,
    });
  }

  async fetchPaymentHistory(
    payload: StorePaymentHistoryRequestPayload
  ): Promise<SATResult<StorePaymentHistoryPageDto, SATError>> {
    await delay(400);
    // 서버 동작 시뮬레이션: 계정 소속 병원 범위 + 필터 + 합계 + 페이지네이션
    let filtered = MOCK_PAYMENT_HISTORY;
    if (payload.status) {
      filtered = filtered.filter((r) => r.status === payload.status);
    }
    if (payload.query) {
      const q = payload.query.toLowerCase();
      filtered = filtered.filter((r) =>
        `${r.orderId} ${r.patientNameMasked} ${r.productName}`.toLowerCase().includes(q)
      );
    }
    const paid = filtered.filter((r) => r.status === "PAID");
    const refunded = filtered.filter((r) => r.status === "REFUNDED");
    const totalPages = Math.max(1, Math.ceil(filtered.length / payload.size));
    const page = Math.min(payload.page, totalPages - 1);
    const items = filtered.slice(page * payload.size, (page + 1) * payload.size);

    return SATResult.success({
      hospitalName: "서울밝은눈안과",
      items,
      page,
      totalPages,
      totalCount: filtered.length,
      summary: {
        paidCount: paid.length,
        paidAmount: paid.reduce((sum, r) => sum + r.amount, 0),
        refundedCount: refunded.length,
        refundedAmount: refunded.reduce((sum, r) => sum + r.amount, 0),
      },
    });
  }

  async fetchSettlements(
    payload: StoreSettlementRequestPayload
  ): Promise<SATResult<StoreSettlementPageDto, SATError>> {
    await delay(400);
    const items = MOCK_PAYMENT_HISTORY.filter((r) => r.status === "PAID")
      .slice(0, 12)
      .map((r, i) => ({
        type: "SALE" as const,
        activatedAt: r.paidAt,
        timeZone: r.timeZone,
        hospitalName: ["서울밝은눈안과", "부산아이사랑안과", "대전밝은미소안과"][i % 3],
        productName: r.productName,
        months: r.months,
        amount: r.amount,
        commission: Math.round(r.amount * 0.1), // 목: 수수료율 10% 가정 (실제 요율은 계약 기준)
        partnerName: "파트너사",
        orderId: r.orderId,
      }));
    return SATResult.success({
      month: payload.month,
      items,
      summary: {
        totalCount: items.length,
        refundCount: 0,
        totalAmount: items.reduce((sum, r) => sum + r.amount, 0),
        totalCommission: items.reduce((sum, r) => sum + r.commission, 0),
      },
    });
  }
}

/** 목 결제 내역 — 로그인 계정 소속 병원(서울밝은눈안과) 범위, 환자명 마스킹 상태 */
const MOCK_PAYMENT_HISTORY: StorePaymentHistoryRecordDto[] = (() => {
  const names = ["김*녀", "이*롱", "박*윤", "최*준", "정*늘", "한*이", "오*빈", "서*우"];
  const products: { name: string; months: number; amount: number }[] = [
    { name: "MyoHabit™ 치료 이용권 3개월", months: 3, amount: 150000 },
    { name: "MyoHabit™ 치료 이용권 6개월", months: 6, amount: 270000 },
    { name: "MyoHabit™ 치료 이용권 12개월", months: 12, amount: 480000 },
    { name: "보호자용 앱 이용권 3개월", months: 3, amount: 30000 },
  ];
  const statuses = ["PAID", "PAID", "PAID", "PAID", "REFUNDED", "PAID", "CANCELED"];
  const records: StorePaymentHistoryRecordDto[] = [];
  for (let i = 0; i < 23; i++) {
    const product = products[i % products.length];
    const paidAt = new Date(2026, 8, 1 - i, 10 + (i % 8), (i * 7) % 60);
    records.push({
      orderId: `ORD-2609-${String(1000 - i).padStart(4, "0")}`,
      paidAt: paidAt.toISOString(),
      timeZone: "Asia/Seoul",
      claimedAt: i % 5 === 0 ? null : paidAt.toISOString(),
      refundedAt: null,
      refundAmount: null,
      refundType: null,
      refundable: true,
      exceptionApproved: false,
      refundRequest: null,
      patientNameMasked: names[i % names.length],
      productName: product.name,
      months: product.months,
      amount: product.amount,
      method: "CARD",
      status: statuses[i % statuses.length],
      receiptUrl: null,
    });
  }
  return records;
})();

/** 목 보호자 (1안 방식 보호자 확인 + 환자 컨텍스트용) */
const MOCK_GUARDIAN: StoreGuardianSearchResponseDto = {
  guardianUuid: "mock-guardian-uuid-0001",
  name: "김보호",
  maskedPhone: "010-****-5678",
  children: [
    {
      patientUuid: "mock-patient-uuid-0001",
      name: "김자녀",
      birthDate: "2017-03-14",
      hasActiveTreatment: false,
      hasUnusedVoucher: false,
    },
    {
      patientUuid: "mock-patient-uuid-0002",
      name: "김둘째",
      birthDate: "2019-11-02",
      hasActiveTreatment: true,
      hasUnusedVoucher: false,
    },
  ],
};
